import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { http } from "@/lib/api";

// Uniform 16:9 grid — iframes cover cleanly without black bars
const layoutSpans = [
    "md:col-span-7 aspect-video",
    "md:col-span-5 aspect-video",
    "md:col-span-5 aspect-video",
    "md:col-span-7 aspect-video",
    "md:col-span-6 aspect-video",
    "md:col-span-6 aspect-video",
    "md:col-span-12 aspect-video",
];

export default function WorkGrid({ limit, showHeader = true }) {
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        http.get("/projects")
            .then((r) => setProjects(r.data || []))
            .catch(() => setProjects([]));
    }, []);

    const items = limit ? projects.slice(0, limit) : projects;

    return (
        <section id="work" data-testid="work-section" className="px-6 md:px-12 py-24 md:py-40">
            {showHeader && (
                <div className="mb-16 md:mb-28 grid grid-cols-12 gap-6 items-end">
                    <div className="col-span-12 md:col-span-6">
                        <p className="text-[11px] tracking-[0.32em] uppercase text-white/45 mb-4">
                            Selected work — 2023 / 2026
                        </p>
                        <h2 className="font-display tracking-tightest leading-[0.82] text-5xl md:text-7xl lg:text-8xl">
                            Historias que
                            <br />
                            <span className="text-[color:var(--midi-accent-strong)]">merecen compartirse.</span>
                        </h2>
                    </div>
                    <div className="hidden md:block col-span-12 md:col-span-5 md:col-start-8 text-white/55 font-light max-w-md text-[15px] leading-relaxed">
                        Cada proyecto es un ejercicio de narrativa, dirección y producción.
                        Una mezcla deliberada entre lo editorial, lo documental y lo comercial.
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                {items.map((p, i) => (
                    <ProjectCard key={p.slug} project={p} span={layoutSpans[i % layoutSpans.length]} index={i} />
                ))}
            </div>

            {limit && projects.length > limit && (
                <div className="mt-20 flex justify-center">
                    <Link
                        to="/work"
                        data-testid="work-view-all"
                        className="midi-link text-[11px] tracking-[0.32em] uppercase text-white/85"
                    >
                        Ver todo el trabajo
                    </Link>
                </div>
            )}
        </section>
    );
}

function buildReelUrl(url) {
    if (!url) return "";
    if (url.includes("youtube.com/embed/")) {
        const id = url.split("/embed/")[1].split("?")[0];
        return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&start=30&controls=0&modestbranding=1&playsinline=1&rel=0`;
    }
    if (url.includes("instagram.com/reel/")) {
        // Instagram blocks autoplay in external iframes — embed shows static cover + play button
        return url.endsWith("/embed") ? url : `${url.replace(/\/$/, "")}/embed`;
    }
    return url + "&background=1&autoplay=1&muted=1&loop=1#t=30s";
}

function ProjectCard({ project, span, index }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (index % 3) * 0.08 }}
            className={`group col-span-1 ${span} relative overflow-hidden`}
            data-testid={`work-card-${project.slug}`}
        >
            <Link to={`/work/${project.slug}`} className="block w-full h-full">
                <div className="absolute inset-0 overflow-hidden">
                    <iframe
                        src={buildReelUrl(project.video_url)}
                        title={project.title}
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                        allow="autoplay; fullscreen; picture-in-picture"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                </div>

                <div className="absolute inset-x-0 bottom-0 px-5 md:px-6 pb-5 md:pb-6 flex flex-col items-start z-10">
                    <p className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-white/55 mb-1.5 transition-colors duration-700 group-hover:text-[color:var(--midi-accent-strong)]">
                        {project.category} — {project.year}
                    </p>
                    <h3 className="font-display tracking-tightest text-lg md:text-xl lg:text-2xl leading-[0.95] text-left">
                        {project.title}
                    </h3>
                </div>
            </Link>
        </motion.article>
    );
}
